/**
 * New API Hooks for TubeOnAI Free Tool
 * Implements hooks for all new API endpoints with proper error handling and streaming
 */

import { useState, useCallback, useEffect } from 'react';
import axios, { AxiosInstance, isAxiosError } from 'axios';
import {
  UserQuotaData,
  SummarizeRequest,
  TranslateRequest,
  MindmapRequest,
  Summary,
  APIError,
} from '../../types/api';

// Get base URL from environment or use default
const getApiBaseUrl = () => {
  // Use the web app URL from store or default
  // The actual URL will be set when making requests
  return 'http://localhost:3000';
};

// Create axios instance - will be configured per request
let api: AxiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'API-Version': 'v1',
  },
});

// Helper to get the correct API URL from store or fallback
export const getApiUrl = (endpoint: string): string => {
  // Try to get from useSummarizer store if available
  if (typeof window !== 'undefined') {
    const getState = (window as any).__SUMMARIZER_STORE__;
    if (typeof getState === 'function') {
      const state = getState();
      if (state?.apiBaseUrl) {
        return `${state.apiBaseUrl}/api/public${endpoint}`;
      }

      // Fallback to web app URL if available
      if (state?.webAppBaseUrl) {
        return `${state.webAppBaseUrl}/api/public${endpoint}`;
      }
    }
  }

  // Final fallback
  return `${getApiBaseUrl()}/api/public${endpoint}`;
};

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const apiError: APIError = {
      code: error.response?.data?.error?.code || 'NETWORK_ERROR',
      message: error.response?.data?.error?.message || error.message,
      details: error.response?.data?.error?.details,
    };
    return Promise.reject(apiError);
  }
);

// Base hook configuration
interface BaseHookConfig {
  onSuccess?: (data: any) => void;
  onError?: (error: APIError) => void;
}

// Quota Management Hook
export function useQuota(config: BaseHookConfig = {}) {
  const [quota, setQuota] = useState<UserQuotaData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<APIError | null>(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);

  const fetchQuota = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(getApiUrl('/quota'), {
        headers: {
          'Content-Type': 'application/json',
          'API-Version': 'v1',
          'X-User-IP': window.localStorage.getItem('user_ip') || '',
        },
      });

      if (response.data.success) {
        setQuota(response.data.data as UserQuotaData);
        setQuotaExceeded(false);
        config.onSuccess?.(response.data.data);
      } else {
        setQuotaExceeded(true);
        const errorData = {
          quota_exceeded: true,
          reset_time: response.data.data?.reset_time,
        };
        setError({
          code: 'QUOTA_EXCEEDED',
          message: response.data.message,
          details: errorData,
        });
        config.onError?.({
          code: 'QUOTA_EXCEEDED',
          message: response.data.message,
          details: errorData,
        });
      }
    } catch (err) {
      const error = err as APIError;
      setError(error);
      config.onError?.(error);
    } finally {
      setLoading(false);
    }
  }, [config]);

  // Auto-fetch quota on mount
  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  return {
    quota,
    loading,
    error,
    quotaExceeded,
    refetch: fetchQuota,
  };
}

// Summarization Hook
export function useSummarize(config: BaseHookConfig = {}) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<APIError | null>(null);

  const summarize = useCallback(
    async (request: SummarizeRequest) => {
      setLoading(true);
      setError(null);
      setSummary(null);

      try {
        const response = await axios.post(getApiUrl('/summarize'), request, {
          headers: {
            'Content-Type': 'application/json',
            'API-Version': 'v1',
          },
        });

        if (response.data.success) {
          setSummary(response.data.data as Summary);
          config.onSuccess?.(response.data.data);
        } else {
          const error: APIError = {
            code: response.data.error?.code || 'PROCESSING_ERROR',
            message: response.data.message,
            details: response.data.error?.details,
          };
          setError(error);
          config.onError?.(error);
        }
      } catch (err) {
        const error = err as APIError;
        setError(error);
        config.onError?.(error);
      } finally {
        setLoading(false);
      }
    },
    [config]
  );

  return {
    summary,
    loading,
    error,
    summarize,
  };
}

// Note: Streaming endpoints should use the generic useCompletion hook
// Example usage for generate-summary:
// const { completion, loading, complete } = useCompletion({
//   api: getApiUrl('/generate-summary'),
//   body: { summary_id: summaryId },
//   onFinish: (prompt, content) => { /* handle completion */ }
// });

// Translation Hook
export function useTranslate(config: BaseHookConfig = {}) {
  const [translatedContent, setTranslatedContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<APIError | null>(null);

  const translate = useCallback(
    async (request: TranslateRequest) => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.post(getApiUrl('/translate'), request, {
          headers: {
            'Content-Type': 'application/json',
            'API-Version': 'v1',
          },
        });

        if (response.data.success && response.data.data) {
          setTranslatedContent(response.data.data);
          config.onSuccess?.(response.data.data);
        } else {
          const error: APIError = {
            code: response.data.error?.code || 'PROCESSING_ERROR',
            message: response.data.message,
            details: response.data.error?.details,
          };
          setError(error);
          config.onError?.(error);
        }
      } catch (err) {
        const error = err as APIError;
        setError(error);
        config.onError?.(error);
      } finally {
        setLoading(false);
      }
    },
    [config]
  );

  return {
    translatedContent,
    loading,
    error,
    translate,
  };
}

// Note: Chat endpoints should use the generic useChat hook
// Example usage for ai-chat:
// const { messages, handleSubmit } = useChat({
//   api: getApiUrl('/ai-chat'),
//   body: { summary_id: summaryId },
//   onFinish: (message) => { /* handle new message */ }
// });

// Mindmap Generation Hook
export function useMindmap(config: BaseHookConfig = {}) {
  const [mindmap, setMindmap] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<APIError | null>(null);

  const generateMindmap = useCallback(
    async (request: MindmapRequest) => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.post(getApiUrl('/mindmap'), request, {
          headers: {
            'Content-Type': 'application/json',
            'API-Version': 'v1',
          },
        });

        if (response.data.success && response.data.data !== undefined) {
          setMindmap(response.data.data);
          config.onSuccess?.(response.data.data);
        } else {
          const error: APIError = {
            code: response.data.error?.code || 'PROCESSING_ERROR',
            message: response.data.message,
            details: response.data.error?.details,
          };
          setError(error);
          config.onError?.(error);
        }

        setLoading(false);
      } catch (err) {
        if (isAxiosError(err)) {
          const errObj = {
            code: err.response?.data?.code || err.code,
            message: err.response?.data?.message || err.message,
            details: err.response?.data?.details,
          };
          setError(errObj);
          config.onError?.(errObj);
          return;
        }

        const error = err as APIError;
        setError(error);
        config.onError?.(error);
      } finally {
        setLoading(false);
      }
    },
    [config]
  );

  return {
    mindmap,
    loading,
    error,
    generateMindmap,
  };
}
